import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { CACHE_EXPIRATION_TIME, DESKTOP_SCREEN_SIZE } from 'app/common/personalConstants';
import {
    ClaimEvent,
    ClaimEventDisplay,
    ClaimUpdatesState
} from 'app/shared/models/claim-tracker.model';
import { ClaimsTrackerService } from 'app/shared/services/claims-tracker.service';
import { LoggerService } from 'app/shared/services/logger.service';
import { SessionService } from 'app/shared/services/session.service';
import { UtilService } from 'app/shared/services/util.service';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
    selector: 'is-claim-updates',
    templateUrl: './claim-updates.component.html',
    styleUrls: ['./claim-updates.component.scss'],
    standalone: false
})
export class ClaimUpdatesComponent implements OnInit {
    @Input() claimNumber!: string;
    @Input() useCaching!: boolean;
    @Output() loadTimeEmitter = new EventEmitter<number>();

    claimUpdates: ClaimEventDisplay[] = [];
    updatesState: ClaimUpdatesState = ClaimUpdatesState.LOADING;
    readonly ClaimUpdatesStateEnum = ClaimUpdatesState;
    private hasLoaded = false;
    private policyNumber!: string;
    private contactPublicId!: string;
    private userType!: string;

    private static readonly LOG_PREFIX = 'ClaimUpdatesComponent';

    /**
     * Event type configuration mapping - Option 2: Dynamic Color via CSS Variables
     *
     * Icons are custom SVG files located in assets/icons/ folder.
     * Icon names correspond to the SVG file names (without .svg extension).
     * Icon colors are specified as hex values and applied dynamically via CSS variables.
     * This allows different colors per icon if needed in the future.
     */
    private readonly EVENT_TYPE_CONFIG: Record<string, { iconName: string; iconColor: string; message: string }> = {
        claimReceived: {
            iconName: 'document-circle',
            iconColor: '#007265ff',
            message: 'We\'ll review your claim, collect any additional information we need, then contact you about next steps.'
        },
        paymentIssued: {
            iconName: 'people-manageparticipants-circle',
            iconColor: '#007265ff',
            message: 'Details are in the Payments section.'
        },
        estimateReceived: {
            iconName: 'agent-circle',
            iconColor: '#007265ff',
            message: 'Your estimate is ready to review. Find it in the Documents section. Let us know if you have questions.'
        },
        repairServiceSelected: {
            iconName: 'construction-circle',
            iconColor: '#007265ff',
            message: 'Your contractor will contact you about your damages. If you don\'t get a call within a business day, feel free to contact them. Find details in the Contractors section.'
        },
        claimClosed: {
            iconName: 'award-circle',
            iconColor: '#007265ff',
            message: 'Thanks for letting us help. Look out for a survey in your email soon. We\'d love your feedback.'
        }
    };

    constructor(
        private claimsTrackerService: ClaimsTrackerService,
        private utils: UtilService,
        private logger: LoggerService,
        private sessionService: SessionService,
        private router: Router
    ) {}

    ngOnInit(): void {
        if (!this.hasLoaded && this.claimNumber) {
            this.logger.getStartLoadTimer();
            this.initializeSessionData();
            this.initializeLogger();

            const UPDATE_CACHE_DATA = this.useCaching ? this.utils.getWithExpiry('UPDATE_DATA') : undefined;

            if (
                typeof UPDATE_CACHE_DATA === 'undefined' ||
                UPDATE_CACHE_DATA.id !== this.claimNumber
            ) {
                this.loadClaimUpdates();
            } else {
                const events: ClaimEvent[] = UPDATE_CACHE_DATA.value.claimEvents;

                if (events.length === 0) {
                    this.updatesState = ClaimUpdatesState.NO_UPDATES;
                    return;
                }

                this.claimUpdates = this.mapEventsToDisplay(events);
                this.updatesState = ClaimUpdatesState.HAS_UPDATES;
            }
            this.hasLoaded = true;
            this.logPageLoadTime();
        }
    }

    private initializeLogger(): void {
        this.logger.setComponentName('ClaimUpdatesComponent');
        this.logger.setPageName('Claim Updates');
        this.logger.info(`${ClaimUpdatesComponent.LOG_PREFIX} - Component Initialized`, {
            claimNumber: this.claimNumber,
            policyNumber: this.policyNumber,
            userType: this.userType
        });
    }

    private initializeSessionData(): void {
        if (!this.claimNumber) {
            this.claimNumber = this.sessionService.getClaimNumber();
        }
        this.contactPublicId = this.sessionService.getSessionItem('claimsContactPublicId');
        this.policyNumber = this.sessionService.getPolicyNumber();
        this.userType = this.sessionService.getUserType();
    }

    private logPageLoadTime(): void {
        const loadTime = this.logger.getStopLoadTimer();
        this.logger.info(`Claim Updates Component Load Time`, loadTime);

        // Emit load time to parent component
        this.loadTimeEmitter.emit(loadTime);
    }

    loadClaimUpdates(): void {
        this.updatesState = ClaimUpdatesState.LOADING;
        this.logger.info(`Loading claim updates for claim: ${this.claimNumber}`);
        this.claimsTrackerService.getClaimEvents(this.claimNumber)
        .pipe(
            catchError((error) => {
                this.logger.error('Property Updates: Error loading claim events', error);
                this.updatesState = ClaimUpdatesState.ERROR;
                return of(null);
            })
        )
        .subscribe((eventsResponse: any) => {
            if (!eventsResponse || !eventsResponse.claimEvents) {
                this.logger.error('Property Updates: No claim events data received');
                this.updatesState = ClaimUpdatesState.ERROR;
                return;
            }

            this.utils.setWithExpiry(
                'UPDATE_DATA',
                eventsResponse,
                CACHE_EXPIRATION_TIME,
                this.claimNumber
            );

            const events: ClaimEvent[] = eventsResponse.claimEvents;
            this.logger.info(`Loaded ${events.length} claim events`);

            if (events.length === 0) {
                this.updatesState = ClaimUpdatesState.NO_UPDATES;
                return;
            }

            this.claimUpdates = this.mapEventsToDisplay(events);
            this.updatesState = ClaimUpdatesState.HAS_UPDATES;
        });
    }

    private mapEventsToDisplay(events: ClaimEvent[]): ClaimEventDisplay[] {
        return events.map((event) => {
            const config = this.EVENT_TYPE_CONFIG[event.eventType];
            let description: string;

            if (event.eventType === 'paymentIssued') {
                description = `${event.eventDescription} ${config.message}`;
            } else {
                description = config?.message || event.eventDescription;
            }

            return {
                iconName: config?.iconName || 'document-circle',
                iconColor: config?.iconColor || '#007265ff',
                eventName: event.eventName,
                description: description,
                eventDate: new Date(event.eventDate)
            };
        });
    }

    @HostListener('window:resize')
    onResize(): void {
        if (window.innerWidth >= DESKTOP_SCREEN_SIZE) {
            this.sessionService.setSessionItem('tab', 'Updates');
            this.router.navigate(['/property/tracker']);
        }
    }
}

